import React from 'react';
import { ACCENT_HEX, HOME_THEME as T } from '../../lib/juniorTheme';
import type { AtrapaRitmoDifficulty } from '../../lib/game/atrapaRitmoEngine';

interface AtrapaRitmoResultsProps {
  score: number;
  hits: number;
  misses: number;
  maxStreak: number;
  difficulty: AtrapaRitmoDifficulty;
  onPlayAgain: () => void;
  onChangeLevel: () => void;
  asModal?: boolean;
}

function gradeEmoji(score: number, hits: number, misses: number): string {
  const accuracy = hits + misses > 0 ? hits / (hits + misses) : 0;
  if (score >= 400 && accuracy >= 0.85) return '🏆';
  if (score >= 250 && accuracy >= 0.7) return '⭐';
  if (score >= 120) return '🎉';
  return '💪';
}

function gradeLabel(score: number, hits: number, misses: number): string {
  const accuracy = hits + misses > 0 ? hits / (hits + misses) : 0;
  if (score >= 400 && accuracy >= 0.85) return '¡Increíble!';
  if (score >= 250 && accuracy >= 0.7) return '¡Muy bien!';
  if (score >= 120) return '¡Buen trabajo!';
  return '¡Sigue practicando!';
}

export const AtrapaRitmoResults: React.FC<AtrapaRitmoResultsProps> = ({
  score,
  hits,
  misses,
  maxStreak,
  difficulty,
  onPlayAgain,
  onChangeLevel,
  asModal = false,
}) => {
  const accent = ACCENT_HEX[difficulty.accent];
  const accuracy = hits + misses > 0 ? Math.round((hits / (hits + misses)) * 100) : 0;

  return (
    <div className={`max-w-md mx-auto text-center space-y-6 ${asModal ? 'w-full' : ''}`}>
      <div
        className={`rounded-[2rem] junior-card-hover ${asModal ? 'p-6 sm:p-7' : 'p-8'}`}
        style={{
          background: 'rgba(255,255,255,0.96)',
          border: `3px solid ${accent}35`,
          boxShadow: `0 20px 48px -16px ${accent}55`,
        }}
      >
        <span className="junior-float text-6xl block mb-3" aria-hidden>
          {gradeEmoji(score, hits, misses)}
        </span>
        <h2 className="junior-display text-3xl" style={{ color: T.text }}>
          {gradeLabel(score, hits, misses)}
        </h2>
        <p className="junior-display text-5xl mt-2" style={{ color: accent }}>
          {score}
        </p>
        <p className="text-xs font-bold uppercase tracking-widest mt-1" style={{ color: T.dim }}>
          puntos
        </p>

        <div className="grid grid-cols-3 gap-3 mt-6">
          {[
            { label: 'Aciertos', value: hits, color: ACCENT_HEX.mint },
            { label: 'Fallos', value: misses, color: ACCENT_HEX.coral },
            { label: 'Mejor racha', value: maxStreak, color: accent },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl py-3 px-2"
              style={{ background: `${s.color}12`, border: `1px solid ${s.color}28` }}
            >
              <p className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: T.dim }}>
                {s.label}
              </p>
              <p className="junior-display text-xl mt-0.5" style={{ color: s.color }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {accuracy > 0 && (
          <p className="text-sm font-semibold mt-4" style={{ color: T.muted }}>
            Precisión: {accuracy}%
          </p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onPlayAgain}
          className="flex-1 py-3.5 rounded-2xl font-extrabold text-white"
          style={{ background: accent, boxShadow: `0 12px 32px -10px ${accent}88` }}
        >
          Otra vez →
        </button>
        <button
          type="button"
          onClick={onChangeLevel}
          className="flex-1 py-3.5 rounded-2xl font-bold junior-glass"
          style={{ color: T.muted }}
        >
          Cambiar nivel
        </button>
      </div>
    </div>
  );
};

export default AtrapaRitmoResults;
